"use client";

import type React from "react";
import { useState, useMemo } from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  Text,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Switch,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Divider,
  Image,
  Tooltip,
  List,
  ListItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FiLogOut, FiPlus, FiChevronDown, FiUsers } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const {
    user,
    signOut,
    isAdmin,
    isSuperAdmin,
    switchToClient,
    currentClientId,
    clients,
    userProfile,
    profileLoading,
    createUser,
    isBalanceNegative,
    impersonating,
    stopImpersonation,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    isAdmin: false,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState<string[]>([]);

  const bgColor =
    "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1629 100%)";
  const textColor = "white";

  const groupedCompanies = useMemo(() => {
    const companyMap = new Map<string, Array<{ id: string; email: string }>>();

    clients.forEach((client) => {
      const companyName = client.company_name;
      if (!companyMap.has(companyName)) {
        companyMap.set(companyName, []);
      }
      companyMap.get(companyName)?.push({
        id: client.id,
        email: client.email,
      });
    });

    return Array.from(companyMap.entries()).map(([companyName, emails]) => ({
      companyName,
      emails,
      defaultClientId: emails[0]?.id || "",
    }));
  }, [clients]);

  const uniqueCompanyNames = useMemo(() => {
    const names = new Set(clients.map((client) => client.company_name));
    return Array.from(names)
      .filter((name) => name)
      .sort();
  }, [clients]);

  const currentClient = clients.find((c) => c.id === currentClientId);
  const displayName =
    isAdmin && currentClient
      ? currentClient.company_name
      : user?.email?.split("@")[0] || "User";

  const profile = {
    full_name: user?.user_metadata?.full_name || null,
    email: user?.email || null,
  };

  const navItems = useMemo(() => [
    { name: "Dashboard",          path: "/dashboard",  roles: null },
    { name: "Agents",             path: "/all-agents", roles: null },
    { name: "Outreach Campaigns", path: "/campaigns",  roles: null },
    { name: "Team",               path: "/team",       roles: ["super_admin", "account_admin"] },
    { name: "Usage History",      path: "/usage",      roles: null },
    { name: "Billing",            path: "/billing",    roles: ["super_admin", "account_admin", "admin"] },
  ].filter(item =>
    item.roles === null ||
    (userProfile !== null && !profileLoading && item.roles.includes(userProfile.role)) ||
    isSuperAdmin ||
    (userProfile?.email?.toLowerCase().endsWith('@leadiumx.ai') ?? false)
  ), [userProfile, profileLoading, isSuperAdmin]);

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
  };

  const handleCreateUser = async () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.companyName ||
      !formData.email
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);
    try {
      const { error } = await createUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        email: formData.email,
        role: (formData.isAdmin ? 'account_admin' : 'user') as any,
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to create user",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          description: `User created and invitation sent to ${formData.email}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setFormData({
          firstName: "",
          lastName: "",
          companyName: "",
          email: "",
          isAdmin: false,
        });
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to create user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCompanyNameChange = (value: string) => {
    handleInputChange("companyName", value);

    if (value.trim()) {
      const filtered = uniqueCompanyNames.filter((name) =>
        name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(filtered.length > 0);
    } else {
      setFilteredCompanies(uniqueCompanyNames);
      setShowCompanyDropdown(uniqueCompanyNames.length > 0);
    }
  };

  const handleSelectCompany = (companyName: string) => {
    handleInputChange("companyName", companyName);
    setShowCompanyDropdown(false);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Top Navigation */}
      <Box
        bgGradient={bgColor}
        py={2}
        px={6}
        position="relative"
        sx={{
          borderBottom: "2px solid transparent",
          borderImage:
            "linear-gradient(90deg, rgba(0, 200, 255, 0.6) 0%, rgba(138, 43, 226, 0.6) 50%, rgba(255, 140, 0, 0.4) 100%)",
          borderImageSlice: 1,
        }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(0, 200, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(138, 43, 226, 0.08) 0%, transparent 50%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          w="full"
          position="relative"
          zIndex={1}
          gap={4}
        >
          {/* LEFT: Logo */}
          <Box>
            <HStack
              spacing={3}
              align="center"
              cursor="pointer"
              onClick={() => navigate("/dashboard")}
              _hover={{ opacity: 0.95 }}
            >
              <Image
                src={process.env.REACT_APP_MAIN_LOGO_URL || "/placeholder.svg"}
                alt="App Logo"
                h="50px"
                objectFit="contain"
                draggable={false}
              />
            </HStack>
          </Box>

          {/* CENTER: Navigation Items */}
          <Flex flex="1" justify="center">
            <HStack spacing={2}>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  variant="ghost"
                  color={isActive(item.path) ? "#00c8ff" : textColor}
                  bg={
                    isActive(item.path)
                      ? "rgba(0, 200, 255, 0.1)"
                      : "transparent"
                  }
                  _hover={{
                    color: "#00c8ff",
                    bg: "rgba(0, 200, 255, 0.08)",
                    transform: "translateY(-1px)",
                  }}
                  fontWeight={isActive(item.path) ? "600" : "500"}
                  fontSize="sm"
                  onClick={() => navigate(item.path)}
                  position="relative"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  transition="all 0.2s ease"
                  _after={
                    isActive(item.path)
                      ? {
                          content: '""',
                          position: "absolute",
                          bottom: "4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "60%",
                          height: "2px",
                          bg: "linear-gradient(90deg, transparent, #00c8ff, transparent)",
                          borderRadius: "2px",
                          boxShadow: "0 0 8px rgba(0, 200, 255, 0.6)",
                        }
                      : {}
                  }
                >
                  {item.name}
                </Button>
              ))}
            </HStack>
          </Flex>

          {/* RIGHT: Client Controls & User Menu */}
          <Box minW="200px">
            <Flex justify="flex-end" align="center">
              <HStack spacing={3}>
                {/* Modern Client Selector for Admin */}
                {isSuperAdmin && (
                  <HStack spacing={2}>
                    {/* Add Client Button */}
                    <Button
                      size="sm"
                      onClick={onOpen}
                      bg="rgba(0, 200, 255, 0.05)"
                      border="1px solid"
                      borderColor="rgba(0, 200, 255, 0.4)"
                      color="#00c8ff"
                      _hover={{
                        bg: "rgba(0, 200, 255, 0.15)",
                        borderColor: "#00c8ff",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 16px rgba(0, 200, 255, 0.3)",
                      }}
                      transition="all 0.2s"
                      borderRadius="lg"
                      leftIcon={<Icon as={FiPlus} />}
                      fontWeight="500"
                    >
                      Add Client
                    </Button>

                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<Icon as={FiChevronDown} />}
                        leftIcon={<Icon as={FiUsers} />}
                        bg="rgba(255, 255, 255, 0.05)"
                        color="white"
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.15)"
                        _hover={{
                          bg: "rgba(255, 255, 255, 0.1)",
                          borderColor: "rgba(0, 200, 255, 0.4)",
                          transform: "translateY(-1px)",
                        }}
                        _active={{ bg: "rgba(255, 255, 255, 0.15)" }}
                        transition="all 0.2s"
                        borderRadius="lg"
                        minW="180px"
                        justifyContent="space-between"
                        fontWeight="500"
                        fontSize="sm"
                      >
                        <Text isTruncated maxW="120px">
                          {currentClient?.company_name || "Select Client"}
                        </Text>
                      </MenuButton>

                      <MenuList
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="xl"
                        borderRadius="lg"
                        py={2}
                        minW="220px"
                        maxW="280px"
                        maxH="400px"
                        overflowY="auto"
                        overflowX="hidden" // ⬅ remove horizontal scroll
                        sx={{
                          scrollbarWidth: "thin", // Firefox
                          scrollbarColor:
                            "var(--chakra-colors-primary-800) transparent",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--chakra-colors-primary-800)",
                            borderRadius: "999px",
                          },
                        }}
                      >
                        {groupedCompanies.length === 0 ? (
                          <MenuItem isDisabled color="gray.500" fontSize="sm">
                            No clients available
                          </MenuItem>
                        ) : (
                          groupedCompanies.map((company) => {
                            const isCurrentCompany = clients.some(
                              (c) =>
                                c.id === currentClientId &&
                                c.company_name === company.companyName
                            );

                            return (
                              <MenuItem
                                key={company.companyName}
                                onClick={() =>
                                  switchToClient(company.defaultClientId)
                                }
                                bg={
                                  isCurrentCompany
                                    ? "rgba(0, 200, 255, 0.1)"
                                    : "transparent"
                                }
                                _hover={{ bg: "rgba(0, 200, 255, 0.05)" }}
                                py={3}
                                px={4}
                                position="relative"
                                borderLeft={
                                  isCurrentCompany
                                    ? "3px solid"
                                    : "3px solid transparent"
                                }
                                borderLeftColor={
                                  isCurrentCompany
                                    ? "primary.800"
                                    : "transparent"
                                }
                              >
                                {/* Tooltip wraps the inner content, not the MenuItem itself */}
                                <Tooltip
                                  hasArrow
                                  placement="right"
                                  bg="gray.800"
                                  color="white"
                                  openDelay={200}
                                  label={
                                    <Box>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        mb={2}
                                        color="cyan.300"
                                      >
                                        Email Accounts:
                                      </Text>
                                      <VStack align="start" spacing={1}>
                                        {company.emails.map((email) => (
                                          <Text
                                            key={email.id}
                                            fontSize="xs"
                                            wordBreak="break-all"
                                          >
                                            • {email.email}
                                          </Text>
                                        ))}
                                      </VStack>
                                    </Box>
                                  }
                                >
                                  <Box w="full">
                                    <VStack align="start" spacing={0} flex={1}>
                                      <Text
                                        fontWeight={
                                          isCurrentCompany
                                            ? "semibold"
                                            : "medium"
                                        }
                                        color={
                                          isCurrentCompany
                                            ? "primary.800"
                                            : "gray.800"
                                        }
                                        isTruncated
                                        maxW="200px"
                                      >
                                        {company.companyName}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {company.emails.length}{" "}
                                        {company.emails.length === 1
                                          ? "account"
                                          : "accounts"}
                                      </Text>
                                    </VStack>
                                  </Box>
                                </Tooltip>
                              </MenuItem>
                            );
                          })
                        )}

                        {groupedCompanies.length > 0 && (
                          <>
                            <Divider my={2} />
                            <MenuItem
                              onClick={onOpen}
                              color="#00c8ff"
                              _hover={{ bg: "rgba(0, 200, 255, 0.05)" }}
                              py={2}
                              px={4}
                            >
                              <HStack>
                                <Icon as={FiPlus} />
                                <Text fontSize="sm">Add New Client</Text>
                              </HStack>
                            </MenuItem>
                          </>
                        )}
                      </MenuList>
                    </Menu>
                  </HStack>
                )}

                {/* User Profile & Sign Out */}
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    rightIcon={<Icon as={FiChevronDown} />}
                    color="white"
                    _hover={{ bg: "rgba(255, 255, 255, 0.08)" }}
                    _active={{ bg: "rgba(255, 255, 255, 0.12)" }}
                    borderRadius="lg"
                    transition="all 0.2s"
                  >
                    <HStack spacing={2}>
                      <Avatar
                        size="sm"
                        name={profile?.full_name || profile?.email || "User"}
                        bg="linear-gradient(135deg, #00c8ff, #8a2be2)"
                        color="white"
                      />
                      <Text
                        fontSize="sm"
                        isTruncated
                        maxW="100px"
                        fontWeight="500"
                      >
                        {profile?.full_name ||
                          profile?.email?.split("@")[0] ||
                          "User"}
                      </Text>
                    </HStack>
                  </MenuButton>
                  <MenuList
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="xl"
                    borderRadius="lg"
                  >
                    <MenuItem
                      onClick={handleSignOut}
                      color="red.500"
                      _hover={{ bg: "red.50" }}
                      icon={<Icon as={FiLogOut} />}
                    >
                      Sign Out
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>
          </Box>
        </Flex>
      </Box>

      {/* Main Content */}
      <Box>
        {isBalanceNegative && !location.pathname.startsWith('/billing') && !location.pathname.startsWith('/agents') && !location.pathname.startsWith('/voice') && (
          <Alert status="error" borderRadius="md" mb={3} mx={6} mt={4}>
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Insufficient Balance</AlertTitle>
              <AlertDescription>
                Your account balance is negative. New calls, SMS, and emails are blocked until you top up.{' '}
                <Button as="a" href="/billing" size="xs" colorScheme="red" variant="solid" ml={2}>
                  Add Balance
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        )}
        {impersonating && (
          <Alert status="warning" borderRadius="md" mb={3} mx={6} mt={4} bg="orange.50" borderColor="orange.300" border="1px solid">
            <AlertIcon color="orange.500" />
            <Box flex="1">
              <AlertTitle color="orange.800">👤 Impersonating: {impersonating.name}</AlertTitle>
              <AlertDescription color="orange.700">
                {impersonating.email} · {impersonating.company}
              </AlertDescription>
            </Box>
            <Button
              size="sm"
              colorScheme="orange"
              variant="solid"
              ml={4}
              onClick={() => { stopImpersonation(); navigate("/team"); }}
            >
              Exit Impersonation
            </Button>
          </Alert>
        )}
        {children}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="gray.800">Create New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack spacing={4} width="100%">
                <FormControl isRequired>
                  <FormLabel color="gray.700">First Name</FormLabel>
                  <Input
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    placeholder="John"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.700">Last Name</FormLabel>
                  <Input
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    placeholder="Doe"
                  />
                </FormControl>
              </HStack>
              <FormControl isRequired>
                <FormLabel color="gray.700">Company Name</FormLabel>
                <Box position="relative">
                  <Input
                    value={formData.companyName}
                    onChange={(e) => handleCompanyNameChange(e.target.value)}
                    onFocus={() => {
                      if (uniqueCompanyNames.length > 0) {
                        setFilteredCompanies(uniqueCompanyNames);
                        setShowCompanyDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on dropdown items
                      setTimeout(() => setShowCompanyDropdown(false), 200);
                    }}
                    placeholder="Acme Corporation"
                    autoComplete="off"
                  />
                  {showCompanyDropdown && filteredCompanies.length > 0 && (
                    <List
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="200px"
                      overflowY="auto"
                      zIndex={10}
                      mt={1}
                    >
                      {filteredCompanies.map((companyName) => (
                        <ListItem
                          key={companyName}
                          px={4}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: "blue.50" }}
                          onClick={() => handleSelectCompany(companyName)}
                          color="gray.700"
                        >
                          {companyName}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </FormControl>
              <FormControl isRequired>
                <FormLabel color="gray.700">Email Address</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="john.doe@company.com"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="admin-switch" mb="0" color="gray.700">
                  Make this user an admin
                </FormLabel>
                <Switch
                  id="admin-switch"
                  isChecked={formData.isAdmin}
                  onChange={(e) =>
                    handleInputChange("isAdmin", e.target.checked)
                  }
                  colorScheme="blue"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateUser}
              isLoading={isCreating}
            >
              Create User & Send Invite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Layout;
